<script>
    import Modal from '@/components/modals/Modal';
    import ButtonSpinner from '@/components/shared/ButtonSpinner';
    import FormCheckbox from '@/components/shared/form/FormCheckbox';
    import utilsService from '@/services/core/utils.service';
    import GsbSearchInput from '@/components/shared/GsbSearchInput';
    import HighlightText from '@/components/shared/HighlightText';
    import sensorsWatchListService from '@/services/sensorsWatchList.service';
    import sensorsService from '@/services/sensors.service';
    import ModalService from '@/services/core/modal.service';

    export default {
        props: {
            modalData: Object
        },
        components: {
            Modal,
            ButtonSpinner,
            FormCheckbox,
            GsbSearchInput,
            HighlightText
        },
        data() {
            return {
                isLoading: 0,
                isLoadingSensors: 0,
                inputSearch: '',
                allSensors: [],
                item: {
                    name: '',
                    sensors: []
                }
            };
        },
        computed: {
            sensors() {
                const sensors = (this.allSensors || []).map(s => ({
                    ...s,
                    checked: this.item.sensors.some(sel => sel.id === s.id)
                }));

                return this.inputSearch ? sensors.filter(s => utilsService.containsSafe(s.name, this.inputSearch)) : sensors;
            }
        },
        methods: {
            async loadSensors() {
                this.isLoadingSensors++;
                try {
                    const sensors = (await sensorsService.all()) || [];
                    const selectedIds = new Set(this.item.sensors.map(sel => sel.id));

                    this.allSensors = sensors
                        .map(s => ({ ...s, checked: selectedIds.has(s.id) }))
                        .sort((a, b) => {
                            if (a.checked !== b.checked) return b.checked - a.checked;
                            return (a.name || '').localeCompare(b.name || '');
                        });
                } finally {
                    this.isLoadingSensors--;
                }
            },
            close() {
                ModalService.closeModal();
            },
            onSensorChecked(sensor, bool) {
                if (bool) this.item.sensors.push(sensor);
                else this.item.sensors = this.item.sensors.filter(s => s.id !== sensor?.id);
            },
            submit() {
                this.isLoading++;
                sensorsWatchListService
                    .create(this.item)
                    .then(() => {
                        this.modalData.onSaveSuccess?.();
                        ModalService.closeModal();
                    })
                    .finally(() => this.isLoading--);
            },
            remove() {
                ModalService.confirm({
                    title: 'Delete Watchlist',
                    msg: `Are you sure you want to delete <b>${this.item.name}</b>?`,
                    onSuccess: () => {
                        this.isLoading++;
                        sensorsWatchListService
                            .delete(this.item.id)
                            .then(() => {
                                this.modalData.onDeleteSuccess?.();
                                ModalService.closeModal();
                            })
                            .finally(() => this.isLoading--);
                    }
                });
            }
        },
        async mounted() {
            await this.loadSensors();

            const { isNew, watchlist } = this.modalData || {};
            if (!isNew && watchlist != null) {
                this.item = {
                    id: watchlist.id,
                    name: watchlist.name,
                    sensors: this.allSensors.filter(s => watchlist.sensorIds?.some(id => id === s.id))
                };
            }
        }
    };
</script>
<template>
    <Modal sizeClass="modal-lg">
        <form @submit.prevent="submit">
            <div class="modal-content">
                <div class="modal-header">
                    <h3 class="modal-title">Sensors Watchlist</h3>
                </div>
                <div class="modal-body">
                    <div class="form-group mb-4">
                        <label class="form-label h5">Name</label>
                        <input type="text" class="form-control" v-model="item.name" required />
                    </div>

                    <div class="row">
                        <div class="col-md">
                            <div class="d-flex gap-3 align-items-center mb-2">
                                <h5 class="mb-0">Pick Sensors</h5>
                                <div class="text-muted">({{ item?.sensors?.length || 0 }} selected)</div>
                            </div>
                            <GsbSearchInput v-if="allSensors.length > 10" v-model="inputSearch" class="mw-none" />
                            <div class="overflow-auto" style="max-height: 30rem">
                                <ul class="list-group list-group-flush">
                                    <li v-if="isLoadingSensors" class="list-group-item text-center text-muted py-3">
                                        <span class="spinner-border spinner-border-sm me-2" />
                                        Loading...
                                    </li>
                                    <template v-else>
                                        <li v-for="(s, i) in sensors" :key="`watchlist-sensors-${i}`" class="list-group-item">
                                            <FormCheckbox :modelValue="s.checked" @change="bool => onSensorChecked(s, bool)" class="mb-0">
                                                <HighlightText :text="s.name" :query="inputSearch" />
                                                <span class="text-muted mx-2">{{ '•' }} </span>
                                                <span class="text-muted small">{{ s.companyName }} / {{ s.batteryName }}</span>
                                            </FormCheckbox>
                                        </li>
                                    </template>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer d-flex justify-content-between">
                    <button v-if="!modalData?.isNew" type="button" class="btn btn-outline-danger" @click="remove">
                        <i class="bi bi-trash text-danger"></i>
                        Delete
                    </button>
                    <div class="d-flex gap-2 ms-auto">
                        <button type="button" class="btn" @click="close">Cancel</button>
                        <ButtonSpinner :isLoading="isLoading" :text="modalData?.isNew ? 'Create' : 'Save'" />
                    </div>
                </div>
            </div>
        </form>
    </Modal>
</template>
